<template>
	<div class="list-container column">
		<div class="title-container">
			<div class="title">{{ $t('roundmap.title') }}</div>
			<div class="dash-select" :class="{ mobile: isMobile }">
				<v-select
					class="panel-select dashboard"
					:value="selectedInfo"
					:options="infos"
					:placeholder="$t('roundmap.select_placeholder')"
					:allowEmpty="false"
					:internalSearch="true"
					:showLabels="false"
					:custom-label="selectLabel"
					@input="selectInfo"
					label="floorName"
					track-by="id"
				>
					<span slot="noResult">{{ $t('roundmap.no_result') }}</span>
					<span
						slot="caret"
						slot-scope="{ toggle }"
						class="caret"
						@mousedown.prevent.stop="toggle()"
					><i class="fas fa-caret-down caret-inner"></i
					></span>
				</v-select>
			</div>
		</div>
		<div class="zoom-controls" style=" touch-action: manipulation;">
		<button @click="adjustZoom(1)">+</button>
		<button @click="adjustZoom(-1)">-</button>
	</div>
<div class="map-container" :style="isMobile ? 'overflow: visible; max-height: 50vh; min-height: 50vh;' : ''">

	
	<div
		id="dropZone"
		ref="dropZone"
		class="box plan-box"
		@contextmenu.prevent
		v-touch:touchhold="openMenu"
		v-touch-options="{ touchHoldTolerance: 1000 }"
		v-touch:moved="(mobileMoved = true)"
		v-touch:end="(mobileMoved = false)"
		@dblclick="openMenu"
		@mousewheel="zoom"
		@mousemove="dragMap"
		@mousedown="startDrag"
		@mouseup="stopDrag"
		@mouseleave="stopDrag"
		@touchstart="startDrag"
		@touchmove="dragMap"
		@touchend="stopDrag"
		:style="`
		padding-top: ${imgRatio * 100}%;
		transform: scale(${zoomLevel}) translate(${mapX}px, ${Math.min(
          500,
          Math.max(-500, mapY)
        )}px);
		transform-origin: top;
		cursor: ${movingMap ? 'grabbing' : 'grab'};
		`"
      >
        <img
          style="pointer-events: none"
          :src="getRoundImage(this.selectedInfo.fileId)"
          alt=""
          class="image-container"
          @load="updateRatio"
          :draggable="false"
        />
        <div
          v-for="(item, index) in itemList"
          :key="itemKey(item, index)"
          :id="'item' + index"
          class="item-container"
          :style="{
            bottom: getPosition(item)[1] * areaHeightRatio - 15 * itemScale(item) + 'px',
            left: getPosition(item)[0] * areaWidthRatio - 15 * itemScale(item) + 'px',
            width: getItemSize(item),
            height: getItemSize(item),
          }"
        >
          <div
            class="circle"
            :style="{
              background: getStatus(item),
              width: `calc(100% + ${12 * itemScale(item)}px)`,
              height: `calc(100% + ${12 * itemScale(item)}px)`,
              'margin-top': `${-6 * itemScale(item)}px`,
              'margin-left': `${-6 * itemScale(item)}px`,
            }"
          ></div>
          <div
            class="placed-item"
            :class="[getItemType(item), { mobile: isMobile }]"
            :style="{ transform: `rotateZ(${item.rotation}deg)` }"
            @click.stop="openMenu($event, index)"
          ></div>
        </div>
        <MenuComponent
          v-model="showMenu"
          :menuX="menuX"
          :menuY="menuY"
          :item="activeItem"
          :showOk="showOk"
          :areaWidthRatio="areaWidthRatio"
          @animated="animating = false"
          @addFlaw="addFlaw"
          @noFlaw="noFlaw"
          @seeInfo="seeInfo"
        />
      </div>
    </div>
  </div>
</template>

<script>
import iconMixin from "@/mixins/iconMixin.js";
import { mapGetters, mapMutations } from "vuex";
import MenuComponent from "./MenuComponent";

export default {
  mixins: [iconMixin],
  components: {
    MenuComponent,
  },
  props: {
    map: Object,
    infos: Array,
  },
  data: () => ({
    lastGroupPos: null,
    minimumScale: 0.3,
    areaWidthRatio: 1,
    areaHeightRatio: 1,
    selectedInfo: {},
    selectedIndex: 0,
    imgRatio: 1,
    showMenu: false,
    menuX: 0,
    menuY: 0,
    animating: false,
    zoomLevel: 1,
    mapX: 0,
    mapY: 0,
    movingMap: false,
    movedMap: false,
    showOk: false,
    activeItem: {},
    activeIndex: 0,
    itemList: [],
    mobileMoved: false,
    roundData: null,
    groupItemClicked: false,
    groupItemIndex: null,
    groupEvent: null,
    startX: 0, // Track initial cursor X position
    startY: 0, // Track initial cursor Y position
    initialMapX: 0, // Track map X position before dragging
    initialMapY: 0,
  }),
  computed: {
    ...mapGetters("appStore", ["isMobile"]),
    ...mapGetters("fileStore", ["getRoundImage"]),
    ...mapGetters("roundHistoryStore", ["activeRound"]),
  },
  watch: {
    activeRound: {
      deep: true,
      handler() {
        const roundData = { ...this.activeRound };
        const idx = roundData.currentInfo ?? 0;
        this.selectedIndex = idx;
        this.selectedInfo = this.infos?.[idx] || this.selectedInfo;

        this.updateItems();
      }
    },
    selectedInfo() {
      this.updateItems();
      this.updateRatio();
      this.updateImg();
    },
  },
  methods: {

    isCheckedThisRound(item) {
      return !!item?.continueWithoutAdding || !!item?.controlled;
    },

    toMs(d) {
      if (!d) return null;

      if (d instanceof Date) {
        const t = d.getTime();
        return Number.isNaN(t) ? null : t;
      }

      let s = String(d).trim();
      if (!s) return null;

      s = s.replace(/(\.\d{3})\d+/, "$1");

      const hasTz = /([zZ]|[+\-]\d{2}:\d{2})$/.test(s);
      if (!hasTz) s += "Z";

      const t = Date.parse(s);
      return Number.isNaN(t) ? null : t;
    },


    hasOpenFlaw(item) {
        const flaws = Array.isArray(item?.flaws) ? item.flaws : [];
        return flaws.some(f => !f?.done);
      },

    hasOpenFlawFromPreviousRound(item) {
        const flaws = Array.isArray(item?.flaws) ? item.flaws : [];
        const open = [...flaws].reverse().find(f => !f?.done);
        if (!open) return false;

        const roundStart = this.toMs(this.activeRound?.created);
        const flawCreated = this.toMs(open?.created);

        if (roundStart == null || flawCreated == null) return true;

        return flawCreated < roundStart;
    },
    getGroupStatusColor(children, color) {
      const items = Array.isArray(children) ? children : [];
      if (!items.length) return color.blue;

      const allChecked = items.every((child) => this.isCheckedThisRound(child));
      if (!allChecked) return color.blue;

      const flawedCount = items.filter((child) => this.hasOpenFlaw(child)).length;
      const okCount = items.length - flawedCount;

      if (flawedCount === items.length) return color.red;
      if (okCount === items.length) return color.green;
      return color.orange;
    },
    itemKey(item, index) {
      if (item?.type === 'Grupp') {
        const childKeys = (item.items || [])
          .map(x => x?.id || x?.mapItemId || x?.clientKey || `${x?.positionX},${x?.positionY}`)
          .join('|');
        return `group:${item.positionX},${item.positionY}:${childKeys}`;
      }

      return (
        item?.id ||
        item?.mapItemId ||
        item?.clientKey ||
        (item?.flawScale ? `loose:${item.flawPositionX},${item.flawPositionY}:${index}` : `idx:${index}`)
      );
    },

    deepClone(obj) { return JSON.parse(JSON.stringify(obj)); },

    getCurrentInfoIndex(roundData) {
      return (roundData?.currentInfo ?? this.selectedIndex ?? 0);
    },

    getInfo(roundData) {
      const idx = this.getCurrentInfoIndex(roundData);
      const info = roundData?.infos?.[idx];
      return { idx, info };
    },
    setInitialZoom(container, img) {
      const containerWidth = container.offsetWidth;
      const containerHeight = container.offsetHeight;

      const imageWidth = img.naturalWidth;
      const imageHeight = img.naturalHeight;

      if (!imageWidth || !imageHeight) return;

      const scaleX = containerWidth / imageWidth;
      const scaleY = containerHeight / imageHeight;

      const zoomToFit = Math.min(scaleX, scaleY);

      this.zoomLevel = zoomToFit;

      this.imgRatio = imageHeight / imageWidth;
    },
    adjustZoom(amount) {
      let scaleFactor = 1.5;
      let newZoom =
        amount > 0 ? this.zoomLevel * scaleFactor : this.zoomLevel / scaleFactor;
      newZoom = Math.max(0.125, Math.min(5.0, newZoom));

      this.zoomLevel = newZoom;

      document.querySelectorAll(".popup").forEach((popup) => {
        popup.style.transform = `scale(${1 / this.zoomLevel})`;
        popup.style.transformOrigin = "top left";
      });

      window.appState = window.appState || {};
      window.appState.zoomLevel = this.zoomLevel;
    },
    startDrag(event) {
      document.body.style.overflow = "hidden";
      this.movingMap = true;
      const isTouchEvent = event.type.startsWith("touch");
      const clientX = isTouchEvent ? event.touches[0].clientX : event.clientX;
      const clientY = isTouchEvent ? event.touches[0].clientY : event.clientY;
      this.startX = clientX;
      this.startY = clientY;
      this.initialMapX = this.mapX;
      this.initialMapY = this.mapY;
    },
    // Drag the map
    dragMap(event) {
      if (!this.movingMap) return;

      const isTouchEvent = event.type.startsWith("touch");
      const clientX = isTouchEvent ? event.touches[0].clientX : event.clientX;
      const clientY = isTouchEvent ? event.touches[0].clientY : event.clientY;

      const deltaX = (clientX - this.startX) / this.zoomLevel;
      const deltaY = (clientY - this.startY) / this.zoomLevel;

      let newX = this.initialMapX + deltaX;
      let newY = this.initialMapY + deltaY;

      const container = document.querySelector(".map-container");
      const dropZone = document.getElementById("dropZone");

      if (!container || !dropZone) return;

      const containerRect = container.getBoundingClientRect();
      const dropZoneRect = dropZone.getBoundingClientRect();

      const minX = -dropZoneRect.width * this.zoomLevel + 50;
      const maxX = containerRect.width - 50;
      const minY = -dropZoneRect.height * this.zoomLevel + 50;
      const maxY = containerRect.height - 50;

      this.mapX = Math.max(minX, Math.min(maxX, newX));
      this.mapY = Math.max(minY, Math.min(maxY, newY));
    },
    // Stop dragging the map
    stopDrag() {
      document.body.style.overflow = "";
      this.movingMap = false;
    },

    ...mapMutations("roundHistoryStore", ["SET_ACTIVEROUND"]),
    updateItems() {
        const roundData = { ...this.activeRound };
        const idx = roundData.currentInfo ?? 0;
        this.selectedInfo = this.infos?.[idx] || this.selectedInfo || {};

        const items = Array.isArray(this.selectedInfo?.roundHistoryItems)
          ? this.selectedInfo.roundHistoryItems
          : [];

        let list = [];
        let groups = [];

        items.forEach((item) => {
        let added = false;
        list.forEach((match) => {
          const matchScale = this.getRawScaleNoZero(match);
          const itemScale = this.getRawScaleNoZero(item);
          const biggestScale = matchScale > itemScale ? matchScale : itemScale;
          const groupDistance = 19 * biggestScale;

          if (
            item.positionX < match.positionX + groupDistance &&
            item.positionX > match.positionX - groupDistance &&
            item.positionY < match.positionY + groupDistance &&
            item.positionY > match.positionY - groupDistance
          ) {
            added = true;
            const groupIndex = groups.findIndex(
              (group) => group.items[0].id === match.id
            );

            if (groupIndex !== -1) {
              const cloned = { ...item };
              cloned.positionX = groups[groupIndex].positionX;
              cloned.positionY = groups[groupIndex].positionY;
              groups[groupIndex].items.push(cloned);
            } else {
              const group = {
                positionX: match.positionX,
                positionY: match.positionY,
                items: [match, item],
                type: "Grupp",
                rotation: 0,
                scale: (matchScale + itemScale) / 2,
              };
              groups.push(group);
            }
          }
        });

        if (!added) {
          list.push(item);
        }

        added = false;
      });
      groups.forEach((group) => {
        const itemIndex = list.findIndex((item) => item.id === group.items[0].id);
        list.splice(itemIndex, 1);
      });
      list = list.concat(groups);

      if (this.selectedInfo.flaws) {
        list = list.concat(this.selectedInfo.flaws);
      }

      this.itemList = list;
    },
    selectLabel({ buildingName, floorName }) {
      return `${buildingName} - ${floorName}`;
    },
    selectInfo(info) {
      this.selectedIndex = this.infos.findIndex((element) => element.id === info.id);
      this.selectedInfo = this.infos[this.selectedIndex];

      let roundData = { ...this.activeRound };
      roundData.currentInfo = this.selectedIndex;
      this.SET_ACTIVEROUND(roundData);

      this.zoomLevel = 1.0;

      document.querySelectorAll(".popup").forEach((popup) => {
        popup.style.transform = `scale(${1 / this.zoomLevel})`;
        popup.style.transformOrigin = "top left";
      });

      window.appState = window.appState || {};
      window.appState.zoomLevel = this.zoomLevel;

      this.mapX = 0;
      this.mapY = 0;

      this.$emit("update");
    },
    updateRatio() {
      const dropzone = document.getElementById("dropZone");

      if (dropzone) {
        this.roundData = { ...this.activeRound };
        this.areaWidthRatio = dropzone.offsetWidth / 1000;
        this.areaHeightRatio = dropzone.offsetHeight / 1000;
        this.roundData.widthRatio = this.areaWidthRatio;
        this.roundData.heightRatio = this.areaHeightRatio;
        this.SET_ACTIVEROUND(this.roundData);
      }
    },
getStatus(item) {
  const color = {
    green: "#00ff1565",
    red:   "#ff000065",
    orange:"#ff7b0065",
    blue:  "#0000ff65",
  };

  if (item?.type === "Grupp") {
    return this.getGroupStatusColor(item.items, color);
  }

  if (item?.flawScale) {
    return item.done ? color.green : color.red;
  }

  const open = this.hasOpenFlaw(item);
  const checkedThisRound = this.isCheckedThisRound(item);

  if (checkedThisRound) return open ? color.red : color.green;
  if (open && this.hasOpenFlawFromPreviousRound(item)) return color.orange;
  return color.blue;
},



    getItemType(item) {
      if (item.type === "Grupp") return "group";

      if (!item.type && !item.inventoryType) return "flaw";

      return this.getType(item.inventoryType).icon;
    },
    itemScale(item) {
      let scale = 1;

      if (item.inventoryType || item.type) {
        scale = item.scale;
      }

      if (item.flawScale) {
        scale = item.flawScale;
      }

      return scale < this.minimumScale
        ? 1 * this.areaWidthRatio
        : scale * this.areaWidthRatio;
    },
    getItemSize(item) {
      return `${Math.round(30 * this.itemScale(item))}px`;
    },
    reopenGroupByPos(pos) {
      if (!pos) return;

      this.$nextTick(() => {
        const gx = Number(pos.x);
        const gy = Number(pos.y);

        const groupIndex = (this.itemList || []).findIndex(it =>
          it?.type === 'Grupp' &&
          Number(it.positionX) === gx &&
          Number(it.positionY) === gy
        );

        if (groupIndex < 0) return;

        this.groupItemClicked = true;
        this.groupItemIndex = groupIndex;
        this.activeIndex = groupIndex;
        this.activeItem = this.itemList[groupIndex];

        const el = document.getElementById('item' + groupIndex);
        if (el) {
          this.menuX = el.offsetLeft;
          this.menuY = el.offsetTop;
        }

        this.showOk = true;
        this.animating = false;
        this.showMenu = true;
      });
    },

    openMenu(event, index) {
      if (event.type === "dblclick" && this.showMenu) return;

      if (this.animating || this.movedMap || this.mobileMoved) {
        return;
      }

      this.activeItem = this.itemList[index];

      if (this.activeItem?.items && this.activeItem?.items.length > 1) {
        this.groupItemClicked = true;
        this.groupItemIndex = index;
        this.groupEvent = event;

        this.lastGroupPos = {
          x: Number(this.activeItem.positionX),
          y: Number(this.activeItem.positionY),
        };
      } else {
        this.groupItemClicked = false;
        this.groupItemIndex = null;
        this.groupEvent = null;
        this.lastGroupPos = null;
      }

      this.activeIndex = index;

      if (index !== undefined) {
        this.showOk = true;
      } else {
        this.showOk = false;
      }

      if (this.isMobile && index === undefined) {
        let touch = event.touches[0];
        this.menuX = touch.clientX - touch.target.offsetLeft - 4.75;
        this.menuY = touch.clientY - touch.target.offsetTop - 4.75;
      } else if (index === undefined) {
        this.menuX = event.offsetX - 20;
        this.menuY = event.offsetY - 20;
      } else {
        this.menuX = event.target.parentNode.offsetLeft;
        this.menuY = event.target.parentNode.offsetTop;
      }

      if (!this.showMenu) {
        this.animating = true;
      }

      this.showMenu = true;
    },
    hideMenu() {
      if (!this.animating || !this.isMobile) {
        this.showMenu = false;
        this.animating = true;
      }
    },
    reopenGroupFromCtx(ctx) {
      if (!ctx?.fromGroup || !ctx?.groupPos) return;

      this.$nextTick(() => {
        const gx = Number(ctx.groupPos.x);
        const gy = Number(ctx.groupPos.y);

        const groupIndex = (this.itemList || []).findIndex(it =>
          it?.type === 'Grupp' &&
          Number(it.positionX) === gx &&
          Number(it.positionY) === gy
        );

        if (groupIndex < 0) return;

        this.groupItemClicked = true;
        this.groupItemIndex = groupIndex;
        this.activeIndex = groupIndex;
        this.activeItem = this.itemList[groupIndex];

        const el = document.getElementById('item' + groupIndex);
        if (el) {
          this.menuX = el.offsetLeft;
          this.menuY = el.offsetTop;
        }

        this.showOk = true;
        this.animating = false;
        this.showMenu = true;
      });
    },
    addFlaw(item) {
      let origin = 'loose';
      let position = null;
      let prefs = {
        infoId: this.selectedInfo?.id || null,
        rowItemId: null,
        mapItemId: null,
        prefCategoryId: null,
        prefCategoryKey: null,

        fromGroup: false,
        groupPos: null,
      };

      if (item && item.id && item.inventoryType) {
        origin = 'row';

        const fp = (this.selectedInfo.roundHistoryItems || [])
          .find(p => String(p.id) === String(item.id));

        prefs.rowItemId = fp?.id || item.id;
        prefs.mapItemId = fp?.mapItemId || item.mapItemId || null;
        prefs.prefCategoryId = fp?.flawCategoryId || fp?.flawCategory?.id || null;
        prefs.prefCategoryKey = fp?.inventoryType ? String(fp.inventoryType).trim() : null;

        if (this.groupItemClicked && this.activeItem?.type === 'Grupp') {
          prefs.fromGroup = true;
          prefs.groupPos = {
            x: this.activeItem.positionX,
            y: this.activeItem.positionY,
          };
        }

        const rowIdx = (this.selectedInfo.roundHistoryItems || [])
          .findIndex(p => String(p.id) === String(prefs.rowItemId));

        this.$emit("addFlaw", rowIdx, origin, prefs);
        return;
    }

  origin = 'loose';
  position = [this.menuX / this.areaWidthRatio, this.menuY / this.areaHeightRatio];
  prefs.mapPos = position;

  this.$emit("addFlaw", -1, origin, prefs);
},


   noFlaw(item) {
    const roundData = this.deepClone(this.activeRound);
    const { idx, info } = this.getInfo(roundData);
    if (!info) return;

    const itemIndex = (info.roundHistoryItems || []).findIndex((p) => p.id === item?.id);

    if (itemIndex !== -1) {
      const updated = { ...info.roundHistoryItems[itemIndex] };
      updated.controlled = new Date();
      updated.userName = roundData.userName; // or this.email if that’s your source
      info.roundHistoryItems[itemIndex] = updated;
    } else {
      if (item) {
        item.controlled = new Date();
        item.userName = roundData.userName;
      }
    }

    roundData.currentInfo = idx;

    this.showMenu = false;


    this.SET_ACTIVEROUND(roundData);  // ✅ makes activeRound update immediately
    this.updateItems();  
    this.$emit("saveState", roundData);
    if (this.groupItemClicked && this.lastGroupPos) {
      this.reopenGroupByPos(this.lastGroupPos);
    }
  },
  flawAdded(item) {
    const roundData = this.deepClone(this.activeRound);
    const { idx, info } = this.getInfo(roundData);
    if (!info) return;

    const protectionIdx = (info.roundHistoryItems || []).findIndex((x) => x.id === item?.id);

    if (protectionIdx !== -1) {
      info.roundHistoryItems[protectionIdx] = item;
      info.roundHistoryItems[protectionIdx].controlled = new Date(); 
    } else {
      info.flaws = Array.isArray(info.flaws) ? info.flaws : [];

      item.controlled = item.controlled || new Date();

      info.flaws.push(item);
    }

    roundData.currentInfo = idx;
    this.showMenu = false;

    this.SET_ACTIVEROUND(roundData);
    this.updateItems();
    this.$emit("saveState", roundData);

    if (this.groupItemClicked) {
      this.showMenu = true;
      this.openMenu(this.groupEvent, this.groupItemIndex);
    }
  },

    zoom(event) {
      let dropZone = document.getElementById("dropZone");
      let pageWidth = dropZone.offsetWidth / 2;
      let pageHeight = dropZone.offsetHeight / 2;

      let zoomFactor = event.deltaY > 0 ? 1.1 : 0.9;
      let newZoomLevel = this.zoomLevel * zoomFactor;

      if (newZoomLevel < 1) newZoomLevel = 1;
      if (newZoomLevel > 4) newZoomLevel = 4;

      let left = event.offsetX;
      let top = event.offsetY;

      if (event.target.tagName === "circle") {
        let menu = document.getElementById("menu");
        left = event.offsetX + menu.offsetLeft - 10;
        top = event.offsetY + menu.offsetTop - 10;
      } else if (
        event.target.className.includes("placed-item") ||
        event.target.className.includes("menu-button")
      ) {
        let offsetTop = 0;
        let offsetLeft = 0;
        let element = event.target.parentElement;

        if (event.target.className.includes("flaw")) {
          offsetLeft = 15;
          offsetTop = -15;
        }
        if (event.target.className.includes("ok")) {
          offsetLeft = 15;
          offsetTop = 45;
        }

        left = element.offsetLeft + event.offsetX + offsetLeft;
        top = element.offsetTop + event.offsetY + offsetTop;
      }

      let zoomX = (left - pageWidth) / this.zoomLevel;
      let zoomY = (top - pageHeight) / this.zoomLevel;

      this.mapX += zoomX * (1 - zoomFactor);
      this.mapY += zoomY * (1 - zoomFactor);

      this.zoomLevel = newZoomLevel;
      window.appState = window.appState || {};
      window.appState.zoomLevel = this.zoomLevel;
    },
    startMove() {
      this.movingMap = true;
    },
    async stopMove() {
      this.movingMap = false;
      function flushQueue() {
        return new Promise((resolve) => setTimeout(resolve, 0));
      }
      await flushQueue().then(() => {
        this.movedMap = false;
      });
    },
    checkMapBounds() {
      const element = document.getElementById("dropZone");
      const zoom =
        this.zoomLevel > 1
          ? this.zoomLevel - (this.zoomLevel - 1) * 0.35
          : this.zoomLevel < 1
          ? this.zoomLevel + (1 - this.zoomLevel) * 0.27
          : 1;
      const width = (element.offsetWidth / zoom) * 0.75;
      const height = (element.offsetHeight / zoom) * 0.75;
      this.mapX = this.mapX > width ? width : this.mapX < -width ? -width : this.mapX;
      this.mapY = this.mapY > height ? width : this.mapY < -height ? -height : this.mapY;
    },
    moveMap(event) {
      if (this.movingMap) {
        this.movedMap = true;
        this.mapX += event.movementX;
        this.mapY += event.movementY;
        this.checkMapBounds();
      }
    },
    updateImg() {
      var img = new Image();
      img.onload = () => {
        this.imageHeight = img.height;
        this.imageWidth = img.width;
        this.imgRatio = this.imageHeight / this.imageWidth;
      };
      img.src = this.getRoundImage(this.selectedInfo.fileId);
    },
    seeInfo(item) {
      this.$emit("seeInfo", item);
    },
    getPosition(item) {
      const hasPos =
        item?.positionX !== undefined &&
        item?.positionX !== null &&
        item?.positionY !== undefined &&
        item?.positionY !== null;

      if (hasPos) return [Number(item.positionX), Number(item.positionY)];

      const hasFlawPos =
        item?.flawPositionX !== undefined &&
        item?.flawPositionX !== null &&
        item?.flawPositionY !== undefined &&
        item?.flawPositionY !== null;

      if (hasFlawPos) return [Number(item.flawPositionX), Number(item.flawPositionY)];

      return [5, 5];
    },
    getRawScaleNoZero(item) {
      return item.scale < this.minimumScale ? 1 : item.scale;
    },
  },
  created() {
    window.addEventListener("resize", this.updateRatio);
    let roundData = { ...this.activeRound };
    this.selectedIndex = roundData.currentInfo;
    this.selectedInfo = this.infos[roundData.currentInfo];
    this.updateImg();
  },
  beforeDestroy() {
    window.removeEventListener("resize", this.updateRatio);
  },
  mounted() {
    const dropZone = document.getElementById("dropZone");
    if (dropZone) {
      dropZone.addEventListener(
        "wheel",
        (event) => {
          if (event.deltaY !== 0) {
            event.stopPropagation();
          }
        },
        { passive: false }
      );
    }

    this.$nextTick(() => {
      const container = this.$refs.dropZone;
      if (!container) return;

      const img = container.querySelector("img");
      if (!img) return;

      if (img.complete) {
        this.setInitialZoom(container, img);
      } else {
        img.onload = () => this.setInitialZoom(container, img);
      }
    });
  },
};
</script>

<style scoped lang="scss">
@import "@/assets/colors.scss";

$border-radius: 1px;

.zoom-controls {
  position: fixed;
  right: 40px;
  margin-top: 20px;
  z-index: 10;
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  width: 50px;
}

.zoom-controls button {
  background-color: #fff;
  border: 1px solid #ccc;
  padding: 10px 15px;
  margin-bottom: 5px;
  cursor: pointer;
  border-radius: 8px;
  box-shadow: 0 2px 5px rgba(0, 0, 0, 0.3);
  transition: background-color 0.2s, transform 0.2s;
  font-size: 18px;
  height: 50px;
  width: 50px;
}

.zoom-controls button:hover {
  background-color: #f0f0f0;
  transform: scale(1.05);
}
.title-container {
  display: flex;
  flex-wrap: wrap;
}
.dash-select {
  margin-top: -0.5rem;
  margin-left: auto;
  &.mobile {
    margin-right: 0.5rem;
    margin-left: auto;
    margin-top: -0.5rem;
    margin-bottom: 0.5rem;
  }
}

.map-container {
  contain: content;
  width: 100%;
  max-height: auto;
  min-height: 80vh;
  overflow: hidden;
  -webkit-touch-callout: none;
  -webkit-user-select: none;
  -khtml-user-select: none;
  -moz-user-select: none;
  -ms-user-select: none;
  user-select: none;
}

.box.plan-box {
  width: 100%;
  padding: 0;
  background-size: contain;
  background-repeat: no-repeat;
  margin-left: auto;
  margin-right: auto;
  border-radius: 1px;
  -webkit-box-shadow: none;
  box-shadow: none;
  cursor: -webkit-grab;
  cursor: grab;
  border: 1px solid;
}

.image-container {
  width: 100%;
  position: absolute;
  top: 0;
  left: 0;
  pointer-events: none;
  -webkit-touch-callout: none; /* iOS Safari */
  -webkit-user-select: none; /* Safari */
  -khtml-user-select: none; /* Konqueror HTML */
  -moz-user-select: none; /* Old versions of Firefox */
  -ms-user-select: none; /* Internet Explorer/Edge */
  user-select: none; /* Non-prefixed version, currently
                                  supported by Chrome, Opera and Firefox */
}
.menu {
  z-index: 2;
  position: absolute;
  pointer-events: none;
  .close-button {
    cursor: pointer;
  }
  .menu-button {
    pointer-events: all;
  }
  .cross {
    stroke: black;
    pointer-events: none;
  }
  .svg-container {
    pointer-events: none;
    svg circle {
      pointer-events: all;
    }
  }
}
.item-container {
  position: absolute;
  .placed-item {
    position: absolute;
    top: 0;
    left: 0;
    background-color: transparent;
    cursor: pointer;
    background-size: contain;
    width: 100%;
    height: 100%;
  }
  .circle {
    position: absolute;
    width: calc(100% + 12px);
    height: calc(100% + 12px);
    margin-top: -6px;
    margin-left: -6px;
    background: #ff000065;
    border-radius: 100%;
    pointer-events: none;
  }
}

.flaw,
.no-flaw {
  &.menu-button {
    background-color: transparent;
    cursor: pointer;
    z-index: 2;
    position: absolute;
    background-size: contain;
  }
}
</style>

<style lang="scss">
.multiselect.panel-select.dashboard {
  min-width: 220px;
  max-width: 220px;
  .caret {
    background-color: #fcfcfc;
  }
  .multiselect__tags {
    background-color: white;

    .multiselect__single,
    .multiselect__input {
      background-color: white;
    }
  }
  .multiselect__content {
    background: white;
  }
}
</style>
